import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Alert, HiddenInput, Input, Modal } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchDriverActions as FetchActions,
  UpdateDriverActions as UpdateActions,
} from 'store/ducks/drivers';
import { DeleteDriverActions as DeleteActions } from 'store/ducks/drivers';
import { UpdateDriverValidator } from 'validators/Drivers';
import * as S from './styles';

interface Props {
  driverId: string | number;
  onUpdate?: () => void;
}

export const DriverUpdateForm: React.FC<Props> = ({ driverId, onUpdate }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const { data: driver } = useSelector((state: RootState) => state.fetchDriver);

  const { loading: updatingDriver, validationErrors } = useSelector(
    (state: RootState) => state.updateDriver
  );

  const { loading: deletingDriver } = useSelector(
    (state: RootState) => state.deleteDriver
  );

  const fetchDriver = useCallback((): void => {
    dispatch(FetchActions.request(driverId));
  }, [dispatch, driverId]);

  const onDriverLoad = useCallback((): void => {
    if (!driver) return;

    const { name, cellPhone, document, id } = driver;

    formRef.current?.setFieldValue('name', name);
    formRef.current?.setFieldValue('document', document);
    formRef.current?.setFieldValue('cellPhone', cellPhone);
    formRef.current?.setFieldValue('driverId', id);
  }, [driver]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateDriverValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(driverId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [driverId, dispatch, handleFormErrors, onSuccess]
  );

  const onDelete = useCallback(() => {
    setModalOpen(false);
    dispatch(DeleteActions.request(driverId, onSuccess));
  }, [dispatch, handleFormErrors, driverId]);

  useEffect(() => {
    fetchDriver();
  }, [fetchDriver]);

  useEffect(() => {
    onDriverLoad();
  }, [onDriverLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <HiddenInput name="driverId" />

        <S.FormRow>
          <Input name="name" label="Nome completo" />
          <Input name="document" label="CPF" />
          <Input name="cellPhone" label="Celular" />
        </S.FormRow>

        <Modal isOpen={modalOpen} onClickOutside={() => setModalOpen(false)}>
          <Alert
            title={'Excluir Motorista'}
            text={'Deseja realmente excluir este motorista?'}
            close={() => setModalOpen(false)}
            action={onDelete}
            labelAction={'Sim'}
          />
        </Modal>
        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/motoristas">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit" disabled={updatingDriver}>
            {updatingDriver ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
          <S.Button
            type="button"
            mood="danger"
            onClick={() => setModalOpen(true)}
            disabled={deletingDriver}
            style={{ marginLeft: 'auto' }}
          >
            {deletingDriver ? <S.ActivityIndicator /> : 'Excluir'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
